import { Form, Popover, DatePicker, InputNumber, Switch } from 'antd'
import * as React from 'react'
import { GraviButton, Horizontal, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import type { EditRateButtonProps, EditRateFormValues } from '../types'
import { useEditRateForm, useRateManagement } from '../hooks'
import dayjs from 'dayjs'

/**
 * Component for editing a point-to-point rate
 */
export function EditRateButton({ rate, pointToPoint, onSuccess, setPointToPoint }: EditRateButtonProps) {
  const { form, popoverVisible, setPopoverVisible, hasEndDate, setHasEndDate, showDistance, setShowDistance } =
    useEditRateForm(rate)

  const { editRate, updateMutation } = useRateManagement(pointToPoint, setPointToPoint, onSuccess)

  /**
   * Handle form submission
   */
  const handleEditRate = async (values: EditRateFormValues) => {
    const success = await editRate(rate, values, hasEndDate, showDistance)
    if (success) {
      setPopoverVisible(false)
    }
  }

  /**
   * Render the edit form
   */
  const renderPopoverContent = () => (
    <Form
      form={form}
      layout='vertical'
      onFinish={handleEditRate}
      initialValues={{
        effective_date: dayjs(rate.effective_from),
        effective_to: rate.effective_to ? dayjs(rate.effective_to) : null,
        rate: rate.rate,
        distance: rate.distance || null,
      }}
    >
      <Form.Item
        name='rate'
        label='Rate'
        rules={[
          { required: true, message: 'Please input a rate' },
          {
            type: 'number',
            min: 0.00001,
            message: 'Rate must be greater than 0',
          },
        ]}
      >
        <InputNumber prefix='$' precision={5} step={1.0} min={0} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item label='Distance' style={{ marginBottom: 16 }}>
        <Horizontal justifyContent='space-between' verticalCenter gap={8} style={{ marginBottom: 8 }}>
          <Switch
            checked={showDistance}
            onChange={(checked) => {
              setShowDistance(checked)
              if (!checked) {
                form.setFieldValue('distance', null)
              }
            }}
          />
        </Horizontal>
        {showDistance ? (
          <Form.Item name='distance' noStyle>
            <InputNumber addonAfter='miles' min={0} precision={0} style={{ width: '100%' }} />
          </Form.Item>
        ) : (
          <Texto appearance='hint'> </Texto>
        )}
      </Form.Item>

      <Form.Item label='Effective From' style={{ marginBottom: 8 }}>
        <Horizontal justifyContent='space-between' alignItems='flex-end' gap={8}>
          <Form.Item
            name='effective_date'
            rules={[{ required: true, message: 'Please select effective date' }]}
            style={{ marginBottom: 0 }}
          >
            <DatePicker format='MM/DD/YYYY' />
          </Form.Item>
        </Horizontal>
      </Form.Item>

      <Form.Item style={{ marginBottom: 16 }}>
        <Horizontal verticalCenter gap={8}>
          <Switch checked={hasEndDate} onChange={setHasEndDate} />
          <Texto>{hasEndDate ? 'End Date' : 'No End Date'}</Texto>
        </Horizontal>
      </Form.Item>

      {hasEndDate && (
        <Form.Item
          name='effective_to'
          label='Effective To'
          rules={[
            { required: hasEndDate, message: 'Please select end date' },
            {
              validator: (_, value) => {
                if (
                  hasEndDate &&
                  value &&
                  form.getFieldValue('effective_date') &&
                  value.isBefore(form.getFieldValue('effective_date'))
                ) {
                  return Promise.reject('End date must be after start date')
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <DatePicker format='MM/DD/YYYY' />
        </Form.Item>
      )}

      <Horizontal gap={8} justifyContent='flex-end'>
        <GraviButton appearance='outlined' onClick={() => setPopoverVisible(false)}>
          Cancel
        </GraviButton>
        <GraviButton appearance='solid' theme2 htmlType='submit' loading={updateMutation.isPending}>
          Save
        </GraviButton>
      </Horizontal>
    </Form>
  )

  return (
    <Popover
      content={renderPopoverContent()}
      title='Edit Rate'
      trigger='click'
      placement='bottomLeft'
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
    >
      <GraviButton icon={<MaterialIcon className='pt-1' size={16} icon='edit' />} />
    </Popover>
  )
}
